import DOMPurify from 'dompurify'
import { Flex, Icon, Stack, Text } from '@backyard-ui/core'
import { messageBoxStyles } from 'frontendMyOrders/plugins/Chat/componentsRefact/MessageBox/messageBox.styles'
import { generateSimpleId } from 'frontendMyOrders/utils'
import { byteToMegaByte } from 'frontendMyOrders/utils/formatters'

import {
  DownloadRegular,
  HomeRegular,
  ImageRegular,
  StoreRegular
} from 'scripts/react-components/shared/icons'

import { AttachmentsType } from '../../types'

const USER_TYPE = {
  BOT: 'bot',
  SELLER: 'seller',
  USER: 'user'
} as const

type UserTypeProps = (typeof USER_TYPE)[keyof typeof USER_TYPE]

export interface MessageBoxProps {
  message: string
  type: UserTypeProps
  name: string
  timestamp: string
  attachments?: AttachmentsType
  isOnMobileApp?: boolean
}

const MessageBox = (props: MessageBoxProps) => {
  const { type, name, timestamp, message, attachments, isOnMobileApp } = props

  const styles = messageBoxStyles({ type })

  const sanitizeMessage = (dirty: string): string =>
    DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: ['strong', 'br', 'a', 'u'],
      ALLOWED_ATTR: ['href', 'target', 'rel']
    })

  return (
    <Flex align="start" className={styles.wrapper()}>
      <Stack direction="column" spacing="4" className={styles.container()}>
        <Flex direction="column" className={styles.labelWrapper()}>
          <Stack align="center" spacing="2" className={styles.labelHeader()}>
            {(type === USER_TYPE.SELLER || type === USER_TYPE.BOT) && (
              <Flex className={styles.avatar()}>
                <Icon color="current" size="md">
                  {type === USER_TYPE.SELLER ? (
                    <StoreRegular />
                  ) : (
                    <HomeRegular />
                  )}
                </Icon>
              </Flex>
            )}
            <Text
              size="md"
              weight="bold"
              UNSAFE_className={styles.headerText()}>
              {name}
            </Text>
            <Text
              size="xl"
              weight="bold"
              UNSAFE_className={styles.headerText()}>
              •
            </Text>
            <Text size="md" UNSAFE_className={styles.headerText()}>
              {timestamp}
            </Text>
          </Stack>
          <div className={styles.content()}>
            <Text size="md" UNSAFE_className={styles.text()} asChild>
              <p
                dangerouslySetInnerHTML={{ __html: sanitizeMessage(message) }}
              />
            </Text>
          </div>
        </Flex>

        {attachments &&
          attachments.length > 0 &&
          attachments.map(attachment => (
            <div key={generateSimpleId()} className={styles.content()}>
              <Stack
                align="center"
                spacing="3"
                direction={type === USER_TYPE.USER ? 'row-reverse' : 'row'}>
                <Flex
                  align="center"
                  justify="center"
                  className={styles.imageWrapper()}>
                  <Icon color="current" size="md">
                    <ImageRegular />
                  </Icon>
                </Flex>

                <Stack
                  spacing="1"
                  direction="column"
                  className={styles.attachmentInfo()}>
                  <Text size="md" UNSAFE_className={styles.text()}>
                    {attachment.name}
                  </Text>
                  <Text size="md" UNSAFE_className={styles.text()}>
                    {byteToMegaByte(attachment.size)}
                  </Text>
                </Stack>

                <Flex align="center" justify="center" asChild>
                  <a
                    href={attachment.url}
                    target={isOnMobileApp ? '_blank' : '_self'}
                    rel="noreferrer"
                    className={styles.attachmentDownloadWrapper()}>
                    <Icon
                      size="md"
                      UNSAFE_className={styles.attachmentDownloadIcon()}>
                      <DownloadRegular />
                    </Icon>
                  </a>
                </Flex>
              </Stack>
            </div>
          ))}
      </Stack>
    </Flex>
  )
}

export default MessageBox
